import skipOnResume from './SkipOnResume';
import next from './Next';
import previous from './Previous';
import valid from './Valid';
import label from './Label';
import data from './Data';
import event from './Event';
import FlowUtil from './../FlowUtil';
import _ from 'underscore';
/**
 * Returns a flow object from a flow definition
 *
 * @param {Function} flowDefn
 * @param {Object} $q - ES6 Promise API implementation
 * @param {Array} extraStepDefnPropFactories - factory functions to add new properties to the step definition
 *
 * @returns {Object} Flow object with an entry, exits, steps.
 */
export default function (flowDefn, $q, extraStepDefnPropFactories) {
    function checkAndSetFlowEntry(stepOrJctToTest, flow) {
        if (stepOrJctToTest) {
            if (flow.entry) {
                throw new Error('Cannot define ' + stepOrJctToTest + ' as an entry as the flow already has an entry point defined: ' + flow.entry.name);
            }
            flow.entry = stepOrJctToTest;
        }
    }

    function stepBuilder(stepNameOrObject, isFlowEntry, flow) {
        function addStepToCurrentAndParentFlows(step, flowObj) {
            if (!flowObj) {
                return;
            }
            flowObj._steps[step.name] = step;
            return addStepToCurrentAndParentFlows(step, flowObj._parent);
        }

        if (FlowUtil.isStepObject(stepNameOrObject)) {
            if (isFlowEntry) {
                checkAndSetFlowEntry(stepNameOrObject, flow);
            }
            return stepNameOrObject;
        }
        if (!_.isString(stepNameOrObject)) {
            throw new Error('Step function takes a string: ' + stepNameOrObject);
        }
        if (flow._steps[stepNameOrObject]) {
            throw new Error('Step with name ' + stepNameOrObject + ' already exists in flow');
        }

        var step = {
            _type: 'STEP',
            name: stepNameOrObject
        };
        step.onNext = next(step, $q);
        step.onPrevious = previous(step);
        step.onEvent = event(step, $q);
        step.isValid = valid(step);
        step.skipOnResumeIf = skipOnResume(step);
        step.label = label(step);
        step.data = data(step);

        if (extraStepDefnPropFactories && _.isArray(extraStepDefnPropFactories)) {
            extraStepDefnPropFactories.forEach(function (propFactory) {
                return propFactory(step);
            });
        }
        if (isFlowEntry) {
            checkAndSetFlowEntry(step, flow);
        }
        addStepToCurrentAndParentFlows(step, flow);
        return step;
    }

    function junctionBuilder(junctionNameOrObject, isFlowEntry, flow) {
        function addJunctionToCurrentAndParentFlows(junction, flowObj) {
            if (!flowObj) {
                return;
            }
            flowObj._junctions[junction.name] = junction;
            return addJunctionToCurrentAndParentFlows(junction, flowObj._parent);
        }

        if (FlowUtil.isJunctionObject(junctionNameOrObject)) {
            if (isFlowEntry) {
                checkAndSetFlowEntry(junctionNameOrObject, flow);
            }
            return junctionNameOrObject;
        }
        if (!_.isString(junctionNameOrObject)) {
            throw new Error('Junction function takes a string: ' + junctionNameOrObject);
        }
        if (flow._junctions[junctionNameOrObject]) {
            throw new Error('Junction with name ' + junctionNameOrObject + ' already exists in flow');
        }

        var junction = {
            _type: 'JUNCTION',
            name: junctionNameOrObject
        };
        junction.onNext = next(junction, $q);
        junction.data = data(junction);

        if (isFlowEntry) {
            checkAndSetFlowEntry(junction, flow);
        }
        addJunctionToCurrentAndParentFlows(junction, flow);
        return junction;
    }

    function flowBuilder(stepFn, junctionFn, parentFlow) {
        var cachedFlows = [];

        return function (flowFn) {
            // cache the result of a flow creation if the same flow definition is encountered
            var cacheHit = _.find(cachedFlows, function (cacheItem) {
                return cacheItem.defn === flowFn;
            });
            if (cacheHit) {
                return cacheHit.flow;
            }
            var currentFlow = {
                entry: undefined,
                exits: undefined,
                _steps: {},
                _junctions: {},
                _parent: parentFlow
            };

            var boundStepFn = _.partial(stepFn, _, _, currentFlow);
            var boundJunctionFn = _.partial(junctionFn, _, _, currentFlow);

            flowFn(boundStepFn, boundJunctionFn, flowBuilder(stepFn, junctionFn, currentFlow));

            // A flow exit is a step or junction that does not have a doAction, branch, or goTo handler defined
            currentFlow.exits = _.pick(currentFlow._steps, function (step) {
                return _.every(_.values(step.onNext), function (prop) {
                    return prop() === undefined;
                });
            });
            // add unbound junctions also
            _.extend(currentFlow.exits, _.pick(currentFlow._junctions, function (junction) {
                return _.every(_.values(junction.onNext), function (prop) {
                    return prop() === undefined;
                });
            }));

            cachedFlows.push({
                defn: flowFn,
                flow: currentFlow
            });
            return currentFlow;
        };
    }

    var flowFn = flowBuilder(stepBuilder, junctionBuilder);
    return flowFn(flowDefn);
}


//////////////////
// WEBPACK FOOTER
// ./app/customer/js/src/edge/platform/flow/build/FlowBuilder.js
// module id = 758
// module chunks = 0